/**
 * useFocusContext — the persistent "current focus" provider for the
 * mentorship workspace.
 *
 * Why
 * ---
 * Phase 4 of the V3 plan introduces a Context Sidebar that follows the
 * user across the mentorship shell — when a mentor opens "Sarah Chen",
 * her summary stays visible even as they navigate to Reviews or
 * Meetings. This file is the data spine for that behavior: a typed
 * Context that owns the current focus and the user's pinned focuses.
 *
 * Phase 1 ships the provider and contract with no consumer. The shell
 * mounts it so downstream phases can plug into a stable API without
 * touching the routing layer again.
 *
 * Invariants
 * ----------
 * - At most one current focus. Setting a new one replaces, never stacks.
 * - At most {@link MAX_PINS} pins. Attempting a 6th is a no-op (defensive;
 *   callers should disable the pin button when the limit is hit).
 * - Pins are de-duplicated by `id` regardless of `kind` — a single
 *   entity cannot be pinned twice.
 * - State is in-memory only (Phase 1). Phase 4 wires localStorage
 *   persistence; this file deliberately stays IO-free to keep tests
 *   deterministic and the provider trivially mountable in test
 *   environments without a DOM `Storage` shim.
 */

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type ReactNode,
} from 'react';
import { menteeDisplayName } from '../shared/menteeDisplayName';

/** The kinds of entities the user can focus on. Stable string literals
 *  so callers can narrow on `kind` and use exhaustiveness checking. */
export type FocusEntityKind = 'mentee' | 'submission' | 'meeting' | 'project';

export type FocusEntity = {
    /** Discriminant for narrowing. */
    kind: FocusEntityKind;
    /** Backend-stable id. Treat as opaque on the frontend. */
    id: string;
    /** Short human label shown in the sidebar. */
    label: string;
    /** Optional secondary line ("Q1 cohort", "Project Alpha", etc.). */
    sublabel?: string;
};

/** Defensive cap — pinning beyond this number degrades the sidebar
 *  density and the value of pinning at all. */
export const MAX_PINS = 5;

export type FocusContextValue = {
    /** The current focus, or `null` if nothing is focused. */
    focus: FocusEntity | null;
    /** Replace the current focus. Pass `null` to clear. Idempotent — a
     *  no-op if the requested focus already matches by id+kind. */
    setFocus: (next: FocusEntity | null) => void;
    /** Convenience wrapper around `setFocus(null)`. */
    clearFocus: () => void;
    /** Read-only pins list. Mutate via {@link pin} / {@link unpin}. */
    pins: ReadonlyArray<FocusEntity>;
    /** Add a pin. No-op if at cap or already pinned (deduped by id). */
    pin: (entity: FocusEntity) => void;
    /** Remove a pin by id. No-op if not currently pinned. */
    unpin: (id: string) => void;
};

const DEFAULT_VALUE: FocusContextValue = {
    focus: null,
    setFocus: () => undefined,
    clearFocus: () => undefined,
    pins: [],
    pin: () => undefined,
    unpin: () => undefined,
};

// Default to the no-op shape so calling `useFocusContext()` outside a
// provider returns a safe, inert value rather than throwing. Mounting
// the provider is the responsibility of MentorshipShell; consumers
// elsewhere in the app get a calm null focus and no-op mutators.
const FocusContext = createContext<FocusContextValue>(DEFAULT_VALUE);

type ProviderProps = {
    children: ReactNode;
    /** Optional seed for tests / future SSR. Defaults to an empty state. */
    initialFocus?: FocusEntity | null;
    initialPins?: ReadonlyArray<FocusEntity>;
};

export function FocusProvider({
    children,
    initialFocus = null,
    initialPins = [],
}: ProviderProps) {
    const [focus, setFocusState] = useState<FocusEntity | null>(initialFocus);
    const [pins, setPins] = useState<ReadonlyArray<FocusEntity>>(initialPins);

    const setFocus = useCallback((next: FocusEntity | null) => {
        setFocusState((prev) => {
            if (next === null) return null;
            // Idempotent: skip the state update if the focus already
            // matches by kind+id. Avoids spurious re-renders down the
            // tree when consumers re-emit the same focus.
            if (prev && prev.kind === next.kind && prev.id === next.id) return prev;
            return next;
        });
    }, []);

    const clearFocus = useCallback(() => {
        setFocusState(null);
    }, []);

    const pin = useCallback((entity: FocusEntity) => {
        setPins((prev) => {
            if (prev.length >= MAX_PINS) return prev;
            if (prev.some((p) => p.id === entity.id)) return prev;
            return [...prev, entity];
        });
    }, []);

    const unpin = useCallback((id: string) => {
        setPins((prev) => {
            const next = prev.filter((p) => p.id !== id);
            // Same-reference shortcut so consumers don't re-render if
            // the id wasn't in the list.
            return next.length === prev.length ? prev : next;
        });
    }, []);

    // Stable value identity so consumers that depend on it via
    // useMemo / useEffect don't churn unless something actually
    // changed.
    const value = useMemo<FocusContextValue>(
        () => ({ focus, setFocus, clearFocus, pins, pin, unpin }),
        [focus, setFocus, clearFocus, pins, pin, unpin],
    );

    return <FocusContext.Provider value={value}>{children}</FocusContext.Provider>;
}

/** Read the current focus context. Safe to call outside a provider —
 *  returns the inert default and never throws. */
export function useFocusContext(): FocusContextValue {
    return useContext(FocusContext);
}


// ── Pure mapping helpers ──────────────────────────────────────────────────
//
// These build a FocusEntity from a wire shape. Pure so they're trivially
// testable without rendering, and so consumers don't reinvent the
// label-resolution logic per take-over surface.

/** Shape of the mentor-side mentee summary the dashboard hands to the
 *  take-over (a UserMini in mentorshipService). We type the helper
 *  against a structural subset so it stays decoupled from that module. */
export type UserLike = {
    id: number;
    first_name?: string;
    last_name?: string;
    email?: string;
};

/** Build a `mentee`-kind FocusEntity from a wire user. Label
 *  resolution delegates to the shared `menteeDisplayName` SSOT
 *  so every mentee-naming surface across the app uses the same
 *  fallback chain (full name → email → "Mentee #id").
 *
 *  Id format: `mentee::<wire-id>` — the kind is part of the id so a
 *  future `submissionToFocusEntity` etc. with the same numeric wire
 *  id can't collide with a mentee in the pins list (where ids are
 *  used as React keys + dedup). The pure `unfocus()` / `unpin(id)`
 *  callers always pass the entity's `id` field, so they keep
 *  working unchanged.  */
export function menteeToFocusEntity(u: UserLike): FocusEntity {
    const label = menteeDisplayName(u);
    const entity: FocusEntity = {
        kind: 'mentee',
        id: `mentee::${u.id}`,
        label,
    };
    // Only include sublabel when it adds new info beyond the label
    // (e.g. label resolved to email already, so sublabel would be
    // redundant). `menteeDisplayName` already trims surrounding
    // whitespace on the email fallback, so the trimmed-vs-label
    // comparison here is apples-to-apples.
    const trimmedEmail = u.email?.trim();
    if (trimmedEmail && trimmedEmail !== label) {
        entity.sublabel = trimmedEmail;
    }
    return entity;
}
